import React, { useCallback, useEffect, useRef, useState } from "react";
import Sidebar from "../components/shared/Sidebar";
import Navbar from "../components/shared/Navbar";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { TrendingUp, Users, Trophy, Percent, UserCircle2, Filter, Download, FileSpreadsheet, FileText } from "lucide-react";
import TagFilterDropdown from "../components/shared/TagFilterDropdown";
import HelpTooltip from "../components/shared/HelpTooltip";

const COLORS = ["#25D366", "#3B82F6", "#8B5CF6", "#06B6D4", "#EF4444", "#64748B"];

const SELECT_CLS = "px-3 py-2 bg-slate-900 border border-slate-800 rounded-lg text-sm text-slate-100 outline-none focus:border-[#25D366]";

const PERIOD_PRESETS = [
  { value: "all", label: "Todo o histórico" },
  { value: "today", label: "Hoje" },
  { value: "7d", label: "Últimos 7 dias" },
  { value: "30d", label: "Últimos 30 dias" },
  { value: "this_month", label: "Este mês" },
  { value: "last_month", label: "Mês passado" },
  { value: "custom", label: "Personalizado" },
];

// Formata um Date pro formato aceito por <input type="date"> (YYYY-MM-DD).
function toDateInputValue(date) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

// Resolve o preset de período (ou datas personalizadas) pros parâmetros
// date_from/date_to que o backend espera (AAAA-MM-DD). "all" = sem filtro,
// mantém o comportamento atual (todo o histórico).
function resolvePeriodRange(preset, customFrom, customTo) {
  const today = new Date();
  switch (preset) {
    case "today":
      return { date_from: toDateInputValue(today), date_to: toDateInputValue(today) };
    case "7d": {
      const from = new Date(today);
      from.setDate(from.getDate() - 6);
      return { date_from: toDateInputValue(from), date_to: toDateInputValue(today) };
    }
    case "30d": {
      const from = new Date(today);
      from.setDate(from.getDate() - 29);
      return { date_from: toDateInputValue(from), date_to: toDateInputValue(today) };
    }
    case "this_month": {
      const from = new Date(today.getFullYear(), today.getMonth(), 1);
      return { date_from: toDateInputValue(from), date_to: toDateInputValue(today) };
    }
    case "last_month": {
      const from = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const to = new Date(today.getFullYear(), today.getMonth(), 0);
      return { date_from: toDateInputValue(from), date_to: toDateInputValue(to) };
    }
    case "custom":
      return { date_from: customFrom || null, date_to: customTo || null };
    default:
      return { date_from: null, date_to: null };
  }
}

export default function Analytics() {
  const [metrics, setMetrics] = useState(null);
  const [team, setTeam] = useState([]);
  const [pipelines, setPipelines] = useState([]);
  const [selectedUser, setSelectedUser] = useState("all");
  const [selectedPipeline, setSelectedPipeline] = useState("all");
  const [periodPreset, setPeriodPreset] = useState("all");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [origins, setOrigins] = useState([]);
  const [selectedOrigin, setSelectedOrigin] = useState("all");
  const [selectedTags, setSelectedTags] = useState([]);
  const [customFieldDefs, setCustomFieldDefs] = useState([]);
  const [selectedCustomField, setSelectedCustomField] = useState("");
  const [exportOpen, setExportOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const exportMenuRef = useRef(null);
  const { user } = useAuth();
  const isAdmin = ["owner", "admin", "super_admin"].includes(user?.role);

  // Monta os query params a partir dos filtros ativos - usado tanto pra
  // carregar a tela (/dashboard/metrics) quanto pra exportar (/dashboard/export/*),
  // garantindo que o arquivo exportado reflete exatamente o que está na tela.
  const buildFilterParams = useCallback(() => {
    const params = new URLSearchParams();
    if (selectedPipeline !== "all") params.set("pipeline_id", selectedPipeline);
    if (isAdmin && selectedUser !== "all") params.set("user_id", selectedUser);
    const { date_from, date_to } = resolvePeriodRange(periodPreset, customFrom, customTo);
    if (date_from) params.set("date_from", date_from);
    if (date_to) params.set("date_to", date_to);
    if (selectedOrigin !== "all") params.set("source_tag", selectedOrigin);
    if (selectedTags.length) params.set("tags", selectedTags.join(","));
    if (selectedCustomField) params.set("custom_field_key", selectedCustomField);
    return params;
  }, [isAdmin, selectedPipeline, selectedUser, periodPreset, customFrom, customTo, selectedOrigin, selectedTags, selectedCustomField]);

  const loadMetrics = useCallback(async () => {
    const suffix = buildFilterParams().toString();
    const { data } = await api.get(`/dashboard/metrics${suffix ? `?${suffix}` : ""}`);
    setMetrics(data);
  }, [buildFilterParams]);

  useEffect(() => {
    loadMetrics().catch(() => {});
  }, [loadMetrics]);

  useEffect(() => {
    if (!exportOpen) return undefined;
    const handleClickOutside = (e) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target)) {
        setExportOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [exportOpen]);

  const handleExport = async (format) => {
    setExportOpen(false);
    setExporting(true);
    try {
      const suffix = buildFilterParams().toString();
      const { data } = await api.get(`/dashboard/export/${format}${suffix ? `?${suffix}` : ""}`, {
        responseType: "blob",
      });
      const blob = new Blob([data], {
        type: format === "excel"
          ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          : "application/pdf",
      });
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = `analytics.${format === "excel" ? "xlsx" : "pdf"}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);
    } catch (err) {
      console.error("Falha ao exportar Analytics:", err);
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    api.get("/pipelines").then(({ data }) => setPipelines(Array.isArray(data) ? data : [])).catch(() => {});
    api.get("/leads/origins").then(({ data }) => setOrigins(Array.isArray(data?.origins) ? data.origins : [])).catch(() => {});
    api.get("/custom-field-definitions").then(({ data }) => {
      setCustomFieldDefs(Array.isArray(data) ? data.filter((def) => def.active !== false) : []);
    }).catch(() => {});
    if (isAdmin) {
      api.get("/team").then(({ data }) => setTeam(Array.isArray(data) ? data : [])).catch(() => {});
    }
  }, [isAdmin]);

  const funnelGroups = metrics?.funnel_groups?.length
    ? metrics.funnel_groups
    : [{ pipeline_id: metrics?.pipeline_id || "default", pipeline_name: "Funil", stages: metrics?.funnel || [] }];

  return (
    <div className="flex h-screen bg-slate-950 overflow-hidden">
      <Sidebar />
      <main className="flex-1 flex flex-col min-w-0 overflow-y-auto">
        <Navbar
          title="Analytics"
          subtitle={
            isAdmin
              ? "Conversão e ROI por canal, origem, funil e responsável"
              : "Conversão e ROI dos seus leads atribuídos"
          }
        />

        <div className="px-6 pt-4">
          <div className="flex flex-wrap items-center gap-3 bg-slate-900/60 border border-slate-800 rounded-xl p-3">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-slate-500">
              <Filter className="w-4 h-4" /> Filtros
            </div>
            <select
              value={selectedPipeline}
              onChange={(e) => setSelectedPipeline(e.target.value)}
              className={SELECT_CLS}
            >
              <option value="all">Todos os funis</option>
              {pipelines.map((pipeline) => (
                <option key={pipeline.id} value={pipeline.id}>
                  {pipeline.name}{pipeline.is_default ? " (padrão)" : ""}
                </option>
              ))}
            </select>
            {isAdmin && (
              <select
                value={selectedUser}
                onChange={(e) => setSelectedUser(e.target.value)}
                className={SELECT_CLS}
              >
                <option value="all">Todos os responsáveis</option>
                {team.map((member) => (
                  <option key={member.id} value={member.id}>
                    {member.name || member.email}
                  </option>
                ))}
              </select>
            )}
            <div className="flex items-center gap-1">
              <select
                value={periodPreset}
                onChange={(e) => setPeriodPreset(e.target.value)}
                className={SELECT_CLS}
                data-testid="analytics-period-select"
              >
                {PERIOD_PRESETS.map((preset) => (
                  <option key={preset.value} value={preset.value}>{preset.label}</option>
                ))}
              </select>
              <HelpTooltip text="Filtra todos os números da tela por período. 'Todo o histórico' considera todos os leads, sem limite de data -- as demais opções filtram pela data de criação do lead (ou de won_at, no caso da Receita Ganha)." />
            </div>
            {periodPreset === "custom" && (
              <>
                <input
                  type="date"
                  value={customFrom}
                  onChange={(e) => setCustomFrom(e.target.value)}
                  className={SELECT_CLS}
                  data-testid="analytics-period-from"
                />
                <input
                  type="date"
                  value={customTo}
                  onChange={(e) => setCustomTo(e.target.value)}
                  className={SELECT_CLS}
                  data-testid="analytics-period-to"
                />
              </>
            )}
            <div className="flex items-center gap-1">
              <select
                value={selectedOrigin}
                onChange={(e) => setSelectedOrigin(e.target.value)}
                className={SELECT_CLS}
                data-testid="analytics-origin-select"
              >
                <option value="all">Todas as origens</option>
                {origins.map((origin) => (
                  <option key={origin} value={origin}>{origin}</option>
                ))}
              </select>
              <HelpTooltip text="Filtra pela origem/etiqueta de origem do lead (ex: Facebook Ads, Indicação, Site) -- a mesma tag registrada quando o lead entrou no CRM." />
            </div>
            <TagFilterDropdown selected={selectedTags} onChange={setSelectedTags} />
            {customFieldDefs.length > 0 && (
              <div className="flex items-center gap-1">
                <select
                  value={selectedCustomField}
                  onChange={(e) => setSelectedCustomField(e.target.value)}
                  className={SELECT_CLS}
                  data-testid="analytics-custom-field-select"
                >
                  <option value="">Campo personalizado</option>
                  {customFieldDefs.map((def) => (
                    <option key={def.id} value={def.key}>{def.label}</option>
                  ))}
                </select>
                <HelpTooltip text="Mostra abaixo um relatório extra: quantos leads têm cada valor deste campo preenchido e quantos desses viraram venda (Ganho). Leads sem o campo preenchido não entram nesse relatório." />
              </div>
            )}
            <div className="ml-auto flex items-center gap-1 relative" ref={exportMenuRef}>
              <button
                type="button"
                onClick={() => setExportOpen((v) => !v)}
                disabled={exporting || !metrics}
                className="flex items-center gap-2 px-3 py-2 bg-slate-900 border border-slate-800 rounded-lg text-sm text-slate-100 hover:border-[#25D366] disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="analytics-export-button"
              >
                <Download className="w-4 h-4" />
                {exporting ? "Exportando..." : "Exportar"}
              </button>
              <HelpTooltip text="Exporta os dados desta tela em Excel ou PDF, respeitando exatamente os filtros ativos no momento (funil, responsável, período, origem, tags e campo personalizado)." />
              {exportOpen && (
                <div className="absolute right-0 mt-1 w-48 bg-slate-900 border border-slate-800 rounded-lg shadow-lg overflow-hidden z-10">
                  <button
                    type="button"
                    onClick={() => handleExport("excel")}
                    className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-slate-200 hover:bg-slate-800 text-left"
                    data-testid="analytics-export-excel"
                  >
                    <FileSpreadsheet className="w-4 h-4 text-[#25D366]" /> Excel (.xlsx)
                  </button>
                  <button
                    type="button"
                    onClick={() => handleExport("pdf")}
                    className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-slate-200 hover:bg-slate-800 text-left"
                    data-testid="analytics-export-pdf"
                  >
                    <FileText className="w-4 h-4 text-red-400" /> PDF
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {!isAdmin && (
          <div className="mx-6 mt-4 flex items-center gap-2 px-4 py-2.5 rounded-lg bg-blue-500/10 border border-blue-500/20 text-blue-300 text-sm">
            <UserCircle2 className="w-4 h-4 shrink-0" />
            <span>Você está visualizando apenas o seu desempenho individual. Peça ao admin para ver o relatório completo da equipe.</span>
          </div>
        )}

        {!metrics ? (
          <div className="flex items-center justify-center flex-1">
            <div className="w-10 h-10 border-2 border-[#25D366] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="px-6 py-5 space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { icon: Users, label: "Total Leads", value: metrics.total_leads, color: "text-slate-200 bg-slate-700/40" },
                { icon: TrendingUp, label: "Valor Pipeline", value: `R$ ${metrics.total_value.toLocaleString("pt-BR")}`, color: "text-blue-400 bg-blue-500/10" },
                { icon: Trophy, label: "Receita Ganha", value: `R$ ${metrics.won_value.toLocaleString("pt-BR")}`, color: "text-[#25D366] bg-[#25D366]/10" },
                { icon: Percent, label: "Conversão", value: `${metrics.conversion_rate}%`, color: "text-purple-400 bg-purple-500/10" },
              ].map((k, i) => (
                <div key={i} className="bg-slate-900/60 border border-slate-800 rounded-xl p-5" data-testid={`analytics-kpi-${i}`}>
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${k.color}`}>
                    <k.icon className="w-5 h-5" />
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mt-4">{k.label}</div>
                  <div className="text-2xl font-bold text-slate-50 mt-1" style={{ fontFamily: "Outfit" }}>{k.value}</div>
                </div>
              ))}
            </div>

            <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-5">
              <h3 className="text-base font-bold text-slate-50 mb-4" style={{ fontFamily: "Outfit" }}>
                Funil de Conversão
              </h3>
              <div className="space-y-5">
                {funnelGroups.map((group) => {
                  const stages = group.stages || [];
                  const max = Math.max(...stages.map((x) => x.count), 1);
                  return (
                    <div key={group.pipeline_id || group.pipeline_name} className="space-y-2">
                      {selectedPipeline === "all" && (
                        <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">
                          {group.pipeline_name}
                        </div>
                      )}
                      {stages.map((f, i) => {
                        const pct = (f.count / max) * 100;
                        return (
                          <div key={`${group.pipeline_id || "pipeline"}-${f.column_id}`} className="flex items-center gap-3" data-testid={`funnel-row-${i}`}>
                            <div className="w-44 text-sm font-semibold text-slate-300 truncate">{f.name}</div>
                            <div className="flex-1 h-8 bg-slate-800 rounded-md overflow-hidden relative">
                              <div
                                className="h-full transition-all flex items-center px-3 text-xs font-bold text-slate-950"
                                style={{
                                  width: `${Math.max(pct, 6)}%`,
                                  backgroundColor: f.is_won ? "#25D366" : f.is_lost ? "#EF4444" : f.color,
                                }}
                              >
                                {f.count}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-5">
                <h3 className="text-base font-bold text-slate-50 mb-4" style={{ fontFamily: "Outfit" }}>
                  ROI por Origem
                </h3>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={metrics.by_source || []}>
                    <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
                    <XAxis dataKey="name" stroke="#64748b" tick={{ fontSize: 11 }} />
                    <YAxis stroke="#64748b" tick={{ fontSize: 11 }} />
                    <Tooltip
                      contentStyle={{ backgroundColor: "#0f172a", border: "1px solid #334155", borderRadius: 8, color: "#f1f5f9" }}
                      formatter={(v) => `R$ ${Number(v).toLocaleString("pt-BR")}`}
                    />
                    <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                      {(metrics.by_source || []).map((_, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-5">
                <h3 className="text-base font-bold text-slate-50 mb-4" style={{ fontFamily: "Outfit" }}>
                  Leads por Canal
                </h3>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={metrics.by_channel || []}>
                    <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
                    <XAxis dataKey="name" stroke="#64748b" tick={{ fontSize: 11 }} />
                    <YAxis stroke="#64748b" tick={{ fontSize: 11 }} />
                    <Tooltip
                      contentStyle={{ backgroundColor: "#0f172a", border: "1px solid #334155", borderRadius: 8, color: "#f1f5f9" }}
                    />
                    <Bar dataKey="count" fill="#25D366" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {metrics.custom_field_report && (
              <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-5" data-testid="analytics-custom-field-report">
                <h3 className="text-base font-bold text-slate-50 mb-1" style={{ fontFamily: "Outfit" }}>
                  Relatório por {metrics.custom_field_report.label}
                </h3>
                <p className="text-xs text-slate-500 mb-4">
                  Contagem de leads por valor deste campo, com destaque para quantos viraram venda (Ganho).
                </p>
                {metrics.custom_field_report.items.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-6">
                    Nenhum lead com esse campo preenchido nos filtros atuais.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {metrics.custom_field_report.items.map((item) => {
                      const wonPct = item.count ? Math.round((item.won / item.count) * 100) : 0;
                      return (
                        <div
                          key={item.value}
                          className="flex flex-wrap items-center justify-between gap-3 bg-slate-950/50 border border-slate-800 rounded-lg px-4 py-3"
                          data-testid={`custom-field-row-${item.value}`}
                        >
                          <div className="text-sm font-semibold text-slate-200 truncate">{item.value}</div>
                          <div className="flex items-center gap-4 text-xs">
                            <span className="text-slate-400">{item.count} lead{item.count === 1 ? "" : "s"}</span>
                            <span className="text-[#25D366] font-semibold">
                              {item.won} ganho{item.won === 1 ? "" : "s"} ({wonPct}%)
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
